import React, { type ReactNode } from 'react'

import { useSiteMetadata } from '@/components/root-element'
import { config } from '@/internal/config/config'

/**
 * The type for a tag.
 */
type Tag = {
  /** The name of the tag. */
  tagName: string
  /** The attributes of the tag. */
  attributes: Record<string, string | null> | null
  /** The content of the tag. */
  content: string | null
}

/**
 * The props type for {@link Seo}.
 */
export type SeoProps = {
  /** The title of the page. */
  title?: string | null
  /** The description of the page. */
  description?: string | null
  /** The location provided by the page router. */
  location?: {
    pathname: string
  }
  data: TemplateQueries | null | undefined
  /** Optional child elements. */
  children?: ReactNode
}

/**
 * Renders SEO meta tags for a page.
 *
 * This component is designed to be used with the Gatsby Head API.
 * See more at: https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/
 *
 * It uses the deduplication feature of Gatsby Head. By assigning the same ID to a tag,
 * we ensure that only one is used. This prevents duplication of meta tags in the document head.
 * See more at: https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/#deduplication
 *
 * @category Component
 */
const Seo: React.FC<SeoProps> = ({
  title,
  description,
  location,
  data,
  children
}) => {
  const { siteUrl } = useSiteMetadata()
  const url = siteUrl + (location?.pathname ?? '')
  const ogImage = `${config.app.ogBaseUrl}${location?.pathname}`
  return (
    <>
      {data?.seoMetaTags?.tags != null &&
        [...(data?.seoMetaTags?.tags as unknown as Tag[])].map((item, i) => {
          const attributes = Object.fromEntries(
            Object.entries(item.attributes ?? {}).filter(
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ([_, value]) => value != null
            )
          )
          const id =
            item?.attributes?.property ??
            item.attributes?.name ??
            (item.tagName === 'title' ? 'title' : `meta-${i}`)
          const key = 'meta-' + i
          if (item.tagName === 'title') {
            return (
              <title key={key} id={id} {...attributes}>
                {item.content}
              </title>
            )
          }
          return <meta key={key} id={id} {...attributes} />
        })}

      <meta id="og:url" property="og:url" content={url} />

      {children}

      {title != null && (
        <>
          <title id="title">{`${title} | Insurance NTT DATA`} </title>
          <meta
            id="og:title"
            property="og:title"
            content={`${title} | Insurance NTT DATA`}
          />
          <meta
            id="twitter:title"
            name="twitter:title"
            content={`${title} | Insurance NTT DATA`}
          />
        </>
      )}
      {description != null && (
        <>
          <meta id="description" name="description" content={description} />
          <meta
            id="og:description"
            property="og:description"
            content={description}
          />
          <meta
            id="twitter:description"
            name="twitter:description"
            content={description}
          />
        </>
      )}
      {Boolean(config.app.ogBaseUrl) && data?.seo?.image?.url == null && (
        <>
          <meta id="og:image" property="og:image" content={ogImage} />
          <meta id="twitter:image" property="twitter:image" content={ogImage} />
          <meta id="og:image:width" property="og:image:width" content="1200" />
          <meta id="og:image:height" property="og:image:height" content="630" />
        </>
      )}
    </>
  )
}

export default Seo
